// src/ProjectRoleManager.js
import React, { useEffect, useState } from 'react';
import api from './apiClient'; // Use centralized client
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';

const ROLE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' },
];

function ProjectRoleManager({
  open,
  onClose,
  projectId,
  projectName,
  workspaceId,
  currentUserId,
  projectOwnerId,
  workspaceOwnerId,
}) {
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [projectRoles, setProjectRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const isOwner = (userId) =>
    Number(userId) === Number(projectOwnerId) ||
    Number(userId) === Number(workspaceOwnerId);

  useEffect(() => {
    if (!open || !projectId || !workspaceId) {
      return;
    }
    loadData();
  }, [open, projectId, workspaceId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [wsRes, prRes] = await Promise.all([
        api.get('/api/workspace-users', { params: { workspace_id: workspaceId } }),
        api.get('/api/project-roles', { params: { project_id: projectId } }),
      ]);

      setWorkspaceUsers(wsRes.data || []);
      setProjectRoles(prRes.data || []);
    } catch (err) {
      console.error('ProjectRoleManager load error:', err);
      setError(err.response?.data?.error || 'Failed to load project team.');
    } finally {
      setLoading(false);
    }
  };

  const getUserProjectRole = (userId) => {
    const row = projectRoles.find((r) => Number(r.user_id) === Number(userId));
    return row ? row.role : '';
  };

  const refreshRoles = async () => {
    if (!projectId) return;
    try {
      const prRes = await api.get('/api/project-roles', { params: { project_id: projectId } });
      setProjectRoles(prRes.data || []);
    } catch (err) {
      console.error('refreshRoles error:', err);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    if (isOwner(userId)) return; // Shouldn't happen, but safety

    const confirmed = window.confirm(
      `Set project role to ${newRole || 'None'} for ${userId}?`
    );
    if (!confirmed) return;

    try {
      if (!newRole) {
        // Delete role
        await api.delete('/api/project-roles', {
          params: {
            project_id: projectId,
            user_id: userId,
            acting_user_id: currentUserId,
          },
        });
      } else {
        // Upsert role
        await api.post('/api/project-roles', {
          project_id: projectId,
          user_id: userId,
          role: newRole,
          acting_user_id: currentUserId,
        });
      }
      await refreshRoles();
      enqueueSnackbar('Role updated successfully', { variant: 'success' });
    } catch (err) {
      console.error('Change project role error:', err);
      enqueueSnackbar(err.response?.data?.error || 'Error updating role', { variant: 'error' });
      loadData(); // Reload on error
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Project Team · {projectName || 'Project'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Manage which workspace members are part of this project and their role.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Project Role</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workspaceUsers.map((user) => {
                const isOwnerRow = isOwner(user.id);
                const currentRole = getUserProjectRole(user.id);

                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                          {user.username}
                        </Typography>
                        {isOwnerRow && (
                          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                            Owner
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isOwnerRow ? (
                        <Typography variant="body2">Owner</Typography>
                      ) : (
                        <Select
                          size="small"
                          value={currentRole || ''}
                          displayEmpty
                          onChange={(e) => handleChangeRole(user.id, e.target.value)}
                          fullWidth
                          disabled={user.id === currentUserId} // Optional: disable self
                        >
                          {ROLE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {workspaceUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No workspace members found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProjectRoleManager;